import React, { useEffect, useRef, useState } from 'react';

const DIRECTION_LOCK_PX = 6;
const EXIT_DURATION_MS = 400;

export default function MobileProgrammeSwipeAction({
  children,
  enabled = true,
  tone = 'add',
  direction = 'both',
  actionLabel,
  actionLabelClassName = 'text-sm',
  compactActionInset = false,
  neutralAtRest = false,
  onAction,
  collapseOnAction = true,
  onTap,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerCancel,
  allowOverflow = false,
  className = '',
}) {
  const [offset, setOffset] = useState(0);
  const [committing, setCommitting] = useState(false);
  const [collapsing, setCollapsing] = useState(false);
  const [lockedHeight, setLockedHeight] = useState(null);
  const offsetRef = useRef(0);
  const gestureRef = useRef(null);
  const suppressClickRef = useRef(false);
  const rootRef = useRef(null);
  const collapseTimerRef = useRef(null);
  const actionTimerRef = useRef(null);
  const resetTimerRef = useRef(null);

  useEffect(() => {
    if (committing) return;
    offsetRef.current = 0;
    setOffset(0);
  }, [tone, committing]);

  useEffect(() => () => {
    window.clearTimeout(collapseTimerRef.current);
    window.clearTimeout(actionTimerRef.current);
    window.clearTimeout(resetTimerRef.current);
  }, []);

  const isMobileTouch = (event) => (
    enabled
    && event.pointerType !== 'mouse'
    && typeof window !== 'undefined'
    && window.matchMedia('(max-width: 767px)').matches
  );

  const resetGesture = () => {
    gestureRef.current = null;
    offsetRef.current = 0;
    setOffset(0);
  };

  const directionIsAllowed = (deltaX) => (
    direction === 'both'
    || (direction === 'right' && deltaX > 0)
    || (direction === 'left' && deltaX < 0)
  );

  const handlePointerDown = (event) => {
    if (committing) {
      event.preventDefault();
      return;
    }

    if (!isMobileTouch(event)) {
      onPointerDown?.(event);
      return;
    }

    event.stopPropagation();
    suppressClickRef.current = true;
    event.currentTarget.setPointerCapture?.(event.pointerId);
    gestureRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      horizontal: false,
      vertical: false,
      externalCancelled: false,
      width: event.currentTarget.getBoundingClientRect().width,
    };
    onPointerDown?.(event);
  };

  const handlePointerMove = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) {
      onPointerMove?.(event);
      return;
    }

    const deltaX = event.clientX - gesture.startX;
    const deltaY = event.clientY - gesture.startY;

    if (!gesture.horizontal && !gesture.vertical) {
      if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < DIRECTION_LOCK_PX) return;
      if (Math.abs(deltaY) > Math.abs(deltaX)) {
        gesture.vertical = true;
        onPointerMove?.(event);
        return;
      }
      gesture.horizontal = true;
      if (!gesture.externalCancelled) {
        gesture.externalCancelled = true;
        onPointerCancel?.(event);
      }
    }

    if (!gesture.horizontal) {
      if (gesture.vertical) onPointerMove?.(event);
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const maxReveal = Math.max(96, gesture.width * 0.72);
    const nextOffset = directionIsAllowed(deltaX)
      ? Math.sign(deltaX) * Math.min(maxReveal, Math.abs(deltaX))
      : 0;
    offsetRef.current = nextOffset;
    setOffset(nextOffset);
  };

  const commitAction = (gesture) => {
    const width = gesture.width || rootRef.current?.getBoundingClientRect().width || 240;
    const direction = offsetRef.current < 0 ? -1 : 1;
    const height = collapseOnAction
      ? rootRef.current?.getBoundingClientRect().height || null
      : null;
    gestureRef.current = null;
    if (height) setLockedHeight(height);
    setCommitting(true);
    offsetRef.current = direction * (width + 24);
    setOffset(direction * (width + 24));

    if (collapseOnAction) {
      collapseTimerRef.current = window.setTimeout(() => {
        setCollapsing(true);
      }, 140);
    }
    actionTimerRef.current = window.setTimeout(() => {
      onAction?.();
    }, EXIT_DURATION_MS);
    resetTimerRef.current = window.setTimeout(() => {
      offsetRef.current = 0;
      setOffset(0);
      setCommitting(false);
      setCollapsing(false);
      setLockedHeight(null);
    }, EXIT_DURATION_MS + 180);
  };

  const handlePointerUp = (event) => {
    const gesture = gestureRef.current;
    if (!gesture || gesture.pointerId !== event.pointerId) {
      onPointerUp?.(event);
      return;
    }

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }

    if (gesture.horizontal) {
      event.preventDefault();
      event.stopPropagation();
      const actionThreshold = Math.min(92, Math.max(58, gesture.width * 0.32));
      const shouldCommit = Math.abs(offsetRef.current) >= actionThreshold;
      if (shouldCommit) {
        commitAction(gesture);
      } else {
        resetGesture();
      }
    } else {
      const wasVertical = gesture.vertical;
      resetGesture();
      if (onPointerUp) {
        onPointerUp(event);
      } else if (!wasVertical) {
        onTap?.();
      }
    }

    window.setTimeout(() => {
      suppressClickRef.current = false;
    }, 0);
  };

  const handlePointerCancel = (event) => {
    if (gestureRef.current?.pointerId === event.pointerId) {
      resetGesture();
      suppressClickRef.current = false;
      onPointerCancel?.(event);
      return;
    }
    onPointerCancel?.(event);
  };

  const handleClickCapture = (event) => {
    if (!suppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  const actionIndicator = (
    <>
      <span className="flex h-[18px] w-[18px] items-center justify-center rounded-full bg-white/20">
        <svg
          className="block h-2.5 w-2.5"
          viewBox="0 0 12 12"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.75"
          strokeLinecap="round"
          aria-hidden
        >
          {tone === 'remove' ? (
            <path d="M2.5 6h7" />
          ) : (
            <path d="M6 2.5v7M2.5 6h7" />
          )}
        </svg>
      </span>
      <span className={`${actionLabelClassName} font-semibold tracking-wide`}>{actionLabel}</span>
    </>
  );

  return (
    <div
      ref={rootRef}
      className={`relative min-w-0 touch-pan-y ${
        allowOverflow ? 'overflow-visible' : 'overflow-hidden'
      } ${className}`}
      style={{
        maxHeight: collapsing ? 0 : lockedHeight ? `${lockedHeight}px` : undefined,
        opacity: collapsing ? 0 : 1,
        transition: lockedHeight
          ? 'max-height 260ms cubic-bezier(0.22, 1, 0.36, 1), opacity 200ms ease-out'
          : undefined,
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerCancel}
      onClickCapture={handleClickCapture}
    >
      {enabled ? (
        <div
          className={`pointer-events-none absolute inset-0 text-white md:hidden ${
            neutralAtRest && offset === 0
              ? 'bg-transparent'
              : tone === 'remove' ? 'bg-red-500' : 'bg-green-500'
          }`}
          aria-hidden
        >
          <div
            className={`absolute inset-y-0 left-0 flex items-center gap-2.5 ${compactActionInset ? 'pl-2' : 'pl-5'}`}
            style={{ opacity: direction !== 'left' && offset > 0 ? 1 : 0 }}
          >
            {actionIndicator}
          </div>
          <div
            className={`absolute inset-y-0 right-0 flex flex-row-reverse items-center gap-2.5 ${compactActionInset ? 'pr-2' : 'pr-5'}`}
            style={{ opacity: direction !== 'right' && offset < 0 ? 1 : 0 }}
          >
            {actionIndicator}
          </div>
        </div>
      ) : null}
      <div
        className="relative min-w-0 bg-surface will-change-transform"
        style={{
          transform: `translate3d(${offset}px, 0, 0)`,
          opacity: committing ? 0.08 : 1,
          transition: gestureRef.current?.horizontal
            ? 'none'
            : 'transform 280ms cubic-bezier(0.22, 1, 0.36, 1), opacity 180ms ease-out',
        }}
      >
        {children}
      </div>
    </div>
  );
}
